<template>

    <div
        class="bg w-full h-screen relative overflow-hidden md:flex-col flex items-center justify-center py-24 md:py-0"
    >
        <form
            class="p-24 md:p-32 w-full max-w-[85%] sm:max-w-[400px] bg-white rounded-6 shadow-custom flex flex-col items-center z-10 self-start md:self-center"
            @submit.prevent="handleSubmit"
            @keydown.enter.prevent="handleSubmit"
        >

            <div class="mb-16 text-primary-80">
                {{ recoveryMode ? $t('auth.two_factor_recover') : $t('auth.two_factor_login') }}
            </div>

            <input-component
                class="mb-16 w-full"
                type="text"
                v-model="form.code"
                ref="twoFactorInput"
                autofocus
                required
            >{{ recoveryMode ? $t('auth.recovery_code') : $t('auth.auth_code') }}
            </input-component>

            <div v-if="hasErrors" class="mb-16 self-start">
                <ul class="text-semantic-error text-body-sm">
                    <li v-for="(error, index) in allErrors" :key="index" class="flex items-center">
                        <svg-icon type="warning" class="max-w-12 max-h-12 mr-4"></svg-icon>
                        <span>{{ error }}</span>
                    </li>
                </ul>
            </div>

            <simple-button
                :loading="form.submitting"
                type="submit"
                display="primary"
                :disabled="!form.code"
                class="mb-16 w-full"
            >{{ $t('auth.login') }}
            </simple-button>

            <button
                class="underline text-primary-60 trans-ease hover:text-primary block mx-auto"
                @click.prevent="toggleRecoveryMode"
            >{{ recoveryMode ? $t('auth.use_auth_code') : $t('auth.use_recovery_code') }}
            </button>

        </form>
    </div>
</template>

<script>
import Form from '../etc/forms/Form';
import Input from '../components/forms/Input.vue';
import SimpleButton from '../components/ui/SimpleButton.vue';
import values from 'lodash/values';
import flatten from 'lodash/flatten';
import * as users from '../services/UserService';
import visitIntended from '../etc/VisitIntended';
import {mapActions} from 'pinia';
import {useAuthenticationStore} from '../state/modules/authentication';
import SvgIcon from '../components/ui/SvgIcon.vue';

export default {
    components: {
        'input-component': Input,
        'simple-button': SimpleButton,
        'svg-icon': SvgIcon,
    },
    head() {
        return {
            title: this.$t('auth.two_factor_authentication'),
        }
    },
    data: function () {
        return {
            recoveryMode: false,
            form: new Form([
                {name: 'code', value: '', rules: []},
            ]),
            redirectAfterLogin: null,
        }
    },
    computed: {
        hasErrors() {
            return values(this.form.errors).some(errors => errors !== undefined);
        },
        allErrors() {
            return flatten(values(this.form.errors).filter(error => error && error.length));
        },
    },
    methods: {
        ...mapActions(useAuthenticationStore, ['setUser']),
        handleSubmit() {
            this.form.submit(data => users.twoFactorAuth({
                [this.recoveryMode ? 'recovery_code' : 'authentication_code']: data.code
            }))
                .then(response => {
                    return this.setUser(response.data);
                })
                .then(() => {
                    window.location.href = this.redirectAfterLogin || '/';
                })
                .catch((error) => {
                    if (error.response.status === 401) {
                        window.location.href = '/login'
                    }
                });
        },
        toggleRecoveryMode() {
            this.recoveryMode = !this.recoveryMode;
            this.form.reset('code', '');
            this.$refs.twoFactorInput.focus();
        }
    },
    mounted() {
        if (this.$route.query.path) {
            this.redirectAfterLogin = this.$route.query.path;
        }

        this.$router.replace({query: {}});
    }
}
</script>

<style scoped>
.bg::before {
    content: '';
    top: 60vh;
    width: 100vw;
    display: inline-block;
    border-radius: 50%;
    background-color: #DBE0FB;
    position: absolute;
    aspect-ratio: 1;
}

.bg > div:first-child {
    background: rgba(255, 255, 255, 0.85);
}

.shadow-custom {
    -webkit-box-shadow: 0 0 27px 3px rgba(0, 0, 0, 0.12);
    -moz-box-shadow: 0 0 27px 3px rgba(0, 0, 0, 0.12);
    box-shadow: 0 0 27px 3px rgba(0, 0, 0, 0.12);
}

@media only screen and (min-width: 768px) {
    .bg::before {
        top: 50vh;
    }
}
</style>
